import { bookingFailureKind } from "../booking/attempt.js";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api.js";
import { useOptionalAuth } from "../auth.js";
import { idempotencyKey } from "../booking/api.js";
import {
  markAppointmentNoShow,
  undoAppointmentNoShow,
  type AppointmentStatusChangeResult,
} from "../booking/appointmentStatus.js";
import {
  diaryPaymentBadge,
  diaryPaymentCanResolveOnSite,
  diaryPaymentClassifications,
  diaryPaymentSourceLabel,
  diaryPaymentStateLabel,
  type DiaryPaymentClassification,
} from "../booking/diaryPayments.js";
import { TutorCancelLesson } from "../booking/TutorCancelLesson.js";
import { TutorMoveLesson } from "../booking/TutorMoveLesson.js";
import { formatDate, formatTime, londonWallClockAttempt } from "../format.js";
import { Drawer } from "../ui/Dialog.js";
import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import {
  diaryActionsForSurface,
  diaryQuickActions,
  type DiaryQuickAction,
} from "./diaryQuickActions.js";
import { isNoShow as isNoShowEntry, type DiaryEntry } from "./entries.js";
import type { Session } from "./grid.js";
import { lessonPriceLabel } from "./lessonPrice.js";
import { BlockSaleWorkflow } from "../booking/BlockSaleWorkflow.js";
import { blockSaleForm } from "../booking/paymentWorkflow.js";
import { PaymentOverridePanel } from "./PaymentOverridePanel.js";
import { PaymentPanel } from "./PaymentPanel.js";
import { PracticeTodoPanel } from "./PracticeTodoPanel.js";
import type { ScheduleChange } from "./pendingPatches.js";
import { UndoPaymentRecord } from "./UndoPaymentRecord.js";
import { MoveLesson } from "../MoveLesson.js";
import { measureAction } from "../telemetry/actionTiming.js";
import { updateLessonAttendance, type LessonAttendanceStatus } from "./lessonAttendance.js";

export type LessonAction =
  | "move"
  | "cancel"
  | "payment"
  | "payment-override"
  | "payment-reversal"
  | "undo-no-show"
  | "add-credits"
  | "practice-todo"
  | "student-assignment"
  | null;

const ATTENDANCE_RECORDABLE = new Set(["approved", "pending"]);

const NAIVE_LONDON_DATETIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?$/;

/**
 * FastAPI reports a moved lesson's `final_start` as a naive Europe/London wall
 * clock (no offset), so `Date.parse` would read it in the browser's zone. The
 * local diary patch needs the real instant; a value that already carries an
 * offset is left alone. The one hour a year with no single London reading (the
 * repeated autumn hour) keeps its original text, as before.
 */
function movedInstant(startsAt: string): string {
  const match = NAIVE_LONDON_DATETIME.exec(startsAt);
  if (match === null) return startsAt;
  const attempt = londonWallClockAttempt(match[1]!, match[2]!);
  return attempt.ok ? attempt.iso : startsAt;
}
const REVERSIBLE_PAYMENT_METHODS = new Set(["on_site", "cash", "card_terminal", "bank_transfer"]);

function initialPaymentForSession(
  session: Session<DiaryEntry>,
  payment: DiaryPaymentClassification | null | undefined,
): DiaryPaymentClassification | null {
  const first = session.bookings.find(
    (entry): entry is DiaryEntry & { anonymous: false } => !entry.anonymous,
  );
  const appointmentId = first?.externalId;
  return typeof appointmentId === "number" && Number.isInteger(appointmentId) && appointmentId > 0 &&
    payment?.appointment_id === appointmentId
    ? payment
    : null;
}

/**
 * Closing a move/cancel flow must re-read the diary rather than merely hide the
 * drawer. Those mutations change the appointment's place in the calendar (or
 * remove it from the active diary entirely), so leaving the existing mirror
 * projection on screen is misleading. Read-only/detail closes stay cheap.
 */
export function closeLessonActionDrawer(
  action: LessonAction,
  onClose: () => void,
  onChanged: () => void,
  studentMatched = false,
  attendanceChanged = false,
): void {
  if (studentMatched || attendanceChanged || action === "move" || action === "cancel") {
    onChanged();
    return;
  }
  onClose();
}

export function LessonActionDrawer({
  session,
  canManage = true,
  participantCount,
  showNotes = true,
  initialAction = null,
  initialMoveTarget = null,
  initialPayment,
  onClose,
  onChanged,
  onRefresh,
  onScheduleChanged = onChanged,
}: {
  session: Session<DiaryEntry>;
  /**
   * The host may make the appointment readable without making it mutable.
   * The diary passes true implicitly; student-record adapters pass only the
   * authority they already had. Backend mutation routes remain authoritative.
   */
  canManage?: boolean;
  /**
   * Known active participants when the host has an authorised on-behalf view
   * but not the diary's named participant projection. The diary naturally uses
   * the number of named bookings it supplied.
   */
  participantCount?: number;
  /**
   * False when the host may manage an appointment but may not read a
   * colleague's lesson note. Diary/own-lesson callers keep notes by default.
   */
  showNotes?: boolean;
  initialAction?: LessonAction;
  initialMoveTarget?: string | null;
  /** Refresh the host's data without closing the drawer after an in-place action. */
  onRefresh?: () => void;
  /** The host's already-loaded classification, still revalidated in the background. */
  initialPayment?: DiaryPaymentClassification | null;
  onClose: () => void;
  onChanged: () => void;
  /** Confirmed schedule writes, separate from ordinary read refreshes. */
  onScheduleChanged?: (change?: ScheduleChange) => void;
}) {
  const publish = usePublishActionOutcome();
  const auth = useOptionalAuth();
  const me = auth?.me ?? null;
  const actionReasonId = useId();
  const cancelReturnFocusRef = useRef<HTMLButtonElement>(null);
  const cancelOpenerRef = useRef<HTMLButtonElement>(null);
  const restoreCancelOpener = useRef(false);
  const cancelReported = useRef(false);
  // `auth === null` means no AuthProvider ancestor exists at all — an
  // isolated component test, never a real signed-out user, since every real
  // staff surface renders inside AuthProvider. That is a different fact from
  // `me === null`, which a real AuthProvider produces too while its own `me`
  // is still loading or genuinely absent — useOptionalAuth's own contract
  // requires permissioned actions to fail closed in that case, not fall back
  // to this test convenience.
  const isolatedHarness = auth === null;
  const [action, setAction] = useState<LessonAction>(initialAction);
  const [payment, setPayment] = useState<DiaryPaymentClassification | null>(() =>
    initialPaymentForSession(session, initialPayment),
  );
  const paymentRequestVersion = useRef(0);
  // Host data (`session`, `initialPayment`) is only a seed for the first
  // render of an appointment. Once this drawer has written, its own post-write
  // read owns the payment state, and a later host re-render with the older seed
  // must never put a stale status back.
  const hostPayment = useRef({ session, initialPayment });
  hostPayment.current = { session, initialPayment };
  const wroteForAppointment = useRef<number | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentProblem, setPaymentProblem] = useState<string | null>(null);
  const [noShowProblem, setNoShowProblem] = useState<string | null>(null);
  const [attendanceUncertain, setAttendanceUncertain] = useState(false);
  const attendancePresented = useRef(false);
  const attendanceAttempt = useRef<{ kind: "no-show" | "undo"; key: string } | null>(null);
  const attendanceInFlight = useRef(false);
  const attendanceNeedsRefresh = useRef(false);
  const [noShowResult, setNoShowResult] = useState<AppointmentStatusChangeResult | null>(null);
  // The reversal's receipt stays in the open drawer, like the no-show receipt,
  // and is handed to the page only when the drawer closes.
  const [undoResult, setUndoResult] = useState<ActionOutcome | null>(null);
  const [noShowOverride, setNoShowOverride] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [matchingBookingId, setMatchingBookingId] = useState<string | null>(null);
  const [studentMatched, setStudentMatched] = useState(false);
  useEffect(() => {
    if (action !== null || !restoreCancelOpener.current) return;
    restoreCancelOpener.current = false;
    cancelOpenerRef.current?.focus({ preventScroll: true });
  }, [action]);
  // Held here rather than in UndoPaymentRecord so a payment reversal keeps one
  // idempotency key across the back button unmounting and remounting that panel.
  const [reversalKey] = useState(() => crypto.randomUUID());

  const mine = session.bookings.filter(
    (entry): entry is DiaryEntry & { anonymous: false } => !entry.anonymous,
  );
  const first = mine[0];
  const initialAttendanceStatus = first?.attendanceStatus;
  const [lessonAttendance, setLessonAttendance] = useState<LessonAttendanceStatus>(
    initialAttendanceStatus === "present" || initialAttendanceStatus === "late"
      ? initialAttendanceStatus
      : null,
  );
  const [lessonAttendanceProblem, setLessonAttendanceProblem] = useState<string | null>(null);
  const [lessonAttendanceSaving, setLessonAttendanceSaving] = useState(false);
  const attendanceBookingId = useRef(first?.bookingId ?? null);
  useEffect(() => {
    if (attendanceBookingId.current === (first?.bookingId ?? null)) return;
    attendanceBookingId.current = first?.bookingId ?? null;
    const status = first?.attendanceStatus;
    setLessonAttendance(status === "present" || status === "late" ? status : null);
    setLessonAttendanceProblem(null);
  }, [first?.attendanceStatus, first?.bookingId]);
  const ameliaAppointmentId =
    typeof first?.externalId === "number" && Number.isInteger(first.externalId) && first.externalId > 0
      ? first.externalId
      : null;
  const hasAmeliaAppointment = ameliaAppointmentId !== null;
  const appointmentStatuses = first === undefined
    ? []
    : [first.status, first.bookingStatus].map((value) => value.toLowerCase());
  const isCancelled = appointmentStatuses.some(status => status === "canceled" || status === "cancelled");
  const recordedNoShow = noShowResult?.appointment.status.toLowerCase() === "no-show";
  const isNoShow =
    noShowOverride ?? (recordedNoShow || (first !== undefined && isNoShowEntry(first)));
  const canMutate = canManage && hasAmeliaAppointment && !isCancelled;
  const studentName = mine.map((entry) => nameOf(entry) ?? "Unknown").join(", ");
  const serviceName = first?.serviceName ?? "Lesson";
  const activeParticipants = participantCount ?? mine.length;
  const lessonPrice = lessonPriceLabel(first?.price, activeParticipants);
  // Notes, practice work and student records are owner/tutor teaching surfaces.
  // Office-only staff can still get appointment actions where their booking
  // capabilities allow them, without being sent into routes their role cannot
  // reach. Permissive only in the isolated-harness case; a real AuthProvider
  // with no resolved `me` fails closed.
  // Somebody allowed to manage every tutor's lessons now sees colleagues'
  // lessons named in the diary, so they can move, cancel or settle them. The
  // teaching side stays the colleague's: notes are author-private and the
  // pupil is not in this person's scope, so those tools are not offered.
  const colleagueLesson =
    me !== null &&
    !me.roles.includes("owner") &&
    me.capabilities.includes("appointments.manage.all") &&
    first !== undefined &&
    first.externalProviderId !== (me.booking.providerId ?? null);
  const canUseTeachingTools =
    !colleagueLesson &&
    (isolatedHarness || (me !== null && (me.roles.includes("owner") || me.roles.includes("tutor"))));
  const canMatchStudent =
    canManage && canUseTeachingTools && me?.booking.staff === true && !isCancelled;
  // The capability check is presentation only: the payment endpoint remains
  // authoritative. Permissive only in the isolated-harness case; a real
  // AuthProvider with no resolved `me` fails closed.
  const canManagePayments = isolatedHarness || (me !== null && me.capabilities.includes("payments.manage"));
  // Reading a payment state and changing one are different questions, and they
  // get different gates. The classification endpoint is keyed only by Amelia
  // appointment id — it takes no student and asks for no payment capability —
  // so anyone who can see the appointment at all can be told whether it is
  // paid. Bundling the two meant an unattributed student or a tutor without
  // `payments.manage` saw no payment state at all, which is how a settled
  // coupon lesson came to look like it had no payment status.
  const paymentReadable = hasAmeliaAppointment;
  const cancelledPaymentAuthority = me !== null && (me.roles.includes("owner") ||
    (me.roles.includes("tutor") && me.booking.providerId != null && me.booking.providerId === first?.externalProviderId));
  const canRecordCancelledPayment = isCancelled && payment?.can_record_cancelled_payment === true && cancelledPaymentAuthority;
  const paymentEligible =
    paymentReadable &&
    (canMutate || (canManage && canRecordCancelledPayment)) &&
    canManagePayments &&
    (activeParticipants === 1 || canRecordCancelledPayment) &&
    first?.studentId !== null &&
    first?.studentId !== undefined;
  // Practice work belongs to the identified private student rather than to the
  // appointment. Keep this separate from canMutate so a read-only appointment
  // does not accidentally become the authority boundary; the route proves the
  // tutor/student relationship again. Group and School lessons are excluded so
  // the drawer never guesses which pupil should receive a task.
  const canSetPracticeTodo =
    canUseTeachingTools &&
    activeParticipants === 1 &&
    first?.studentId !== null &&
    first?.studentId !== undefined &&
    !first.isSchools;
  // One block-sale entry for an identified private student. blockSaleForm keeps
  // the five-credit form deliberately not gated on canMutate/canManage like the
  // other actions: registering a standard five-credit block is inherent for a
  // tutor acting for their own student. The provider id is the tutor's verified
  // own provider, and the Hub/FastAPI boundaries independently re-check
  // student/provider scope. Only the managed form needs canManage.
  const blockSale =
    !colleagueLesson &&
    activeParticipants === 1 &&
    first?.studentId !== null &&
    first?.studentId !== undefined &&
    !first.isSchools
      ? blockSaleForm(me, { canManage })
      : null;
  const startsAt = Date.parse(session.startsAt);
  const noShowEligible =
    canMutate &&
    activeParticipants === 1 &&
    session.bookings.length === 1 &&
    first !== undefined &&
    !first.isSchools &&
    Number.isFinite(startsAt) &&
    startsAt < Date.now() &&
    ATTENDANCE_RECORDABLE.has(first.status.toLowerCase()) &&
    ATTENDANCE_RECORDABLE.has(first.bookingStatus.toLowerCase());
  const attendanceChipsEligible =
    canUseTeachingTools &&
    activeParticipants === 1 &&
    session.bookings.length === 1 &&
    first !== undefined &&
    first.studentId !== null &&
    !first.isSchools &&
    Number.isFinite(startsAt) &&
    startsAt <= Date.now() &&
    !isCancelled &&
    !isNoShow;

  useEffect(() => {
    let current = true;
    const requestVersion = ++paymentRequestVersion.current;
    if (ameliaAppointmentId !== null && wroteForAppointment.current === ameliaAppointmentId) {
      return () => { current = false; };
    }
    const seededPayment = initialPaymentForSession(hostPayment.current.session, hostPayment.current.initialPayment);
    setPayment(seededPayment);
    setPaymentProblem(null);
    if (!paymentReadable || ameliaAppointmentId === null) {
      setPaymentLoading(false);
      return () => { current = false; };
    }

    setPaymentLoading(seededPayment === null);
    void diaryPaymentClassifications([ameliaAppointmentId])
      .then((rows) => {
        if (!current || paymentRequestVersion.current !== requestVersion) return;
        setPayment(rows.find((row) => row.appointment_id === ameliaAppointmentId) ?? null);
      })
      .catch((cause: unknown) => {
        if (!current || paymentRequestVersion.current !== requestVersion) return;
        setPaymentProblem(cause instanceof Error ? cause.message : "Payment state is unavailable.");
      })
      .finally(() => {
        if (current && paymentRequestVersion.current === requestVersion) setPaymentLoading(false);
      });

    return () => { current = false; };
  }, [ameliaAppointmentId, paymentReadable]);

  function changedInPlace() {
    setAction(null);
    if (ameliaAppointmentId !== null) {
      wroteForAppointment.current = ameliaAppointmentId;
      const requestVersion = ++paymentRequestVersion.current;
      setPayment(null);
      setPaymentProblem(null);
      setPaymentLoading(true);
      void diaryPaymentClassifications([ameliaAppointmentId])
        .then((rows) => {
          if (paymentRequestVersion.current !== requestVersion) return;
          setPayment(rows.find((row) => row.appointment_id === ameliaAppointmentId) ?? null);
        })
        .catch((cause: unknown) => {
          if (paymentRequestVersion.current !== requestVersion) return;
          setPaymentProblem(cause instanceof Error ? cause.message : "Payment state is unavailable.");
        })
        .finally(() => {
          if (paymentRequestVersion.current === requestVersion) setPaymentLoading(false);
        });
    }
    if (onRefresh) onRefresh();
    else onChanged();
  }

  if (!first) return null;

  const badge = diaryPaymentBadge(payment);
  const paymentSource = diaryPaymentSourceLabel(payment);
  const canTakePayment = paymentEligible && diaryPaymentCanResolveOnSite(payment);
  const paymentAlreadyCovered = payment?.status === "paid" ||
    (payment?.method === "lesson_credit" && payment.status === "not_required");
  const paymentDisabledReason = paymentEligible && !isCancelled && paymentAlreadyCovered
    ? payment?.method === "lesson_credit" || payment?.method === "coupon"
      ? "A lesson credit or coupon is already applied to this lesson."
      : "This lesson is already paid."
    : undefined;
  const canReverseMoneyPayment =
    !isCancelled && paymentEligible &&
    payment !== null &&
    payment.status === "paid" &&
    !payment.payment_override &&
    payment.payment_id !== null &&
    REVERSIBLE_PAYMENT_METHODS.has(payment.method);
  const canReverseCredit =
    !isCancelled && paymentEligible &&
    payment !== null &&
    payment.method === "lesson_credit" &&
    (payment.status === "paid" || payment.status === "not_required");
  const ownPaymentLesson = canMutate && activeParticipants === 1 &&
    me !== null && (me.roles.includes("tutor") || me.roles.includes("owner")) &&
    me.booking.providerId != null && me.booking.providerId === first.externalProviderId;
  const canOverridePayment = ownPaymentLesson && payment != null &&
    (Boolean(payment.payment_override) || (payment.booking_id != null &&
      ["pending", "partially_paid", "failed"].includes(payment.status) &&
      payment.amount_due != null && Number(payment.amount_due) > 0));
  const canReversePayment = canReverseMoneyPayment || canReverseCredit;

  const paymentState = (() => {
    if (payment === null) return null;
    if (payment.status === "paid") {
      if (badge === "£") return paymentSource === null ? "Paid" : `Paid · ${paymentSource}`;
      if (badge === "*") {
        const state = diaryPaymentStateLabel(payment, session.endsAt);
        return state === "Unknown" ? "Payment status unknown" : state;
      }
    }
    if (payment.method === "lesson_credit" && payment.status === "not_required") {
      return "Paid · Lesson credit";
    }
    const state = diaryPaymentStateLabel(payment, session.endsAt);
    return state === "Unknown" ? "Payment status unknown" : state;
  })();
  const paymentNeedsAttention =
    payment?.status === "pending" ||
    payment?.status === "partially_paid" ||
    payment?.status === "failed" ||
    payment?.status === "unknown";

  const registeredActions = diaryQuickActions({
    entries: mine.map((entry) => ({
      bookingId: entry.bookingId,
      studentId: entry.studentId,
      studentName: nameOf(entry),
      lessonId: entry.lessonId,
      isSchools: entry.isSchools,
    })),
    participantCount: activeParticipants,
    showNotes: showNotes && canUseTeachingTools,
    canViewStudent: canUseTeachingTools,
    canMutate,
    canTakePayment,
    paymentDisabledReason,
    canRecordCancelledPayment,
    canSetPracticeTodo,
    blockSale,
    noShowEligible,
    noShow: isNoShow,
    canReversePayment,
    canMatchStudent,
    cancelled: isCancelled,
  });
  const statusActions = diaryActionsForSurface(registeredActions, "status", { includeDisabled: true });
  const paymentAction = statusActions.find((registered) => registered.action === "record-payment");
  const reversePaymentAction = statusActions.find((registered) => registered.action === "mark-unpaid");
  const attendanceActions = statusActions.filter((registered) =>
    registered.action === "mark-no-show" || registered.action === "undo-no-show",
  ).filter((registered) =>
    !(noShowResult !== null && registered.action === "undo-no-show") &&
    !(attendanceUncertain && attendanceAttempt.current?.kind === "undo" && registered.action === "undo-no-show"),
  );
  const primaryActions = diaryActionsForSurface(registeredActions, "primary", { includeDisabled: true });
  const quickActions = diaryActionsForSurface(registeredActions, "quick");
  const moreActions = diaryActionsForSurface(registeredActions, "more");

  function openRegisteredAction(registered: DiaryQuickAction) {
    if (!registered.enabled) return;
    switch (registered.action) {
      case "change-lesson":
        setAction("move");
        return;
      case "record-payment":
        setAction("payment");
        return;
      case "assign-practice":
        setAction("practice-todo");
        return;
      case "add-credits":
        setAction("add-credits");
        return;
      case "mark-no-show":
        void confirmNoShow();
        return;
      case "undo-no-show":
        void confirmUndoNoShow();
        return;
      case "mark-unpaid":
        setAction("payment-reversal");
        return;
      case "cancel-lesson":
        setAction("cancel");
        return;
      case "attribute-booking":
        if (registered.bookingId === undefined) return;
        setMatchingBookingId(registered.bookingId);
        setAction("student-assignment");
        return;
      case "write-note":
      case "view-student":
        return;
    }
  }

  function renderRegisteredAction(registered: DiaryQuickAction) {
    const className =
      registered.emphasis === "primary"
        ? "action-primary"
        : registered.emphasis === "secondary"
          ? "action-secondary"
          : registered.emphasis === "danger"
            ? "action-danger"
            : "action-tertiary";

    if (registered.href !== undefined) {
      return (
        <Link className={`button ${className}`} key={registered.id} to={registered.href}>
          {registered.label}
        </Link>
      );
    }

    const disabledReason = registered.enabled ? undefined : registered.disabledReason;
    const reasonId = disabledReason ? `${actionReasonId}-${registered.id}` : undefined;
    const button = (
      <button
        className={className}
        key={registered.id}
        ref={registered.action === "cancel-lesson" ? cancelOpenerRef : undefined}
        type="button"
        disabled={!registered.enabled || busy}
        aria-describedby={reasonId}
        onClick={() => openRegisteredAction(registered)}
      >
        {registered.label}
      </button>
    );
    return disabledReason ? (
      <div key={registered.id}>
        {button}
        <p id={reasonId} className="small muted cal-lesson-action-reason">{disabledReason}</p>
      </div>
    ) : button;
  }

  function attendanceReceipt(): ActionOutcome | null {
    if (attendanceUncertain && noShowProblem) return { variant: "uncertain", title: "Attendance outcome needs checking", description: studentName,
      details: [{ value: noShowProblem }, { value: "Check the authoritative lesson and credit state before retrying." }] };
    if (undoResult) return undoResult;
    if (noShowResult) return {
      variant: noShowResult.credit_review_required || paymentProblem ? "attention" : "success", title: "No-show recorded",
      description: `${studentName} · ${formatDate(session.startsAt)} · ${formatTime(session.startsAt)}`,
      details: [{ value: noShowResult.credits_restored === 0 ? "The booking service restored no lesson credits." : `The booking service reports ${noShowResult.credits_restored} lesson credit${noShowResult.credits_restored === 1 ? "" : "s"} restored.` },
        ...(noShowResult.credit_review_required ? [{ value: "The lesson credit effect needs review. Check the authoritative credits before another correction." }] : []),
        ...(paymentProblem ? [{ value: `Payment state could not be refreshed: ${paymentProblem}` }] : [])],
    };
    return null;
  }
  function retainAttendanceReceipt() { const outcome = attendanceReceipt(); if (outcome) publish(outcome, { announce: !attendancePresented.current }); }

  function attendanceKey(kind: "no-show" | "undo"): string {
    if (attendanceAttempt.current?.kind !== kind) attendanceAttempt.current = { kind, key: idempotencyKey() };
    return attendanceAttempt.current.key;
  }

  function attendanceFailureIsUncertain(cause: unknown): boolean {
    if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500) return false;
    return bookingFailureKind(cause) === "uncertain";
  }

  async function confirmNoShow() {
    if (!noShowEligible || busy || attendanceInFlight.current) return;
    if (ameliaAppointmentId === null) return;
    attendanceInFlight.current = true;
    const key = attendanceKey("no-show");
    attendancePresented.current = false;
    setBusy(true);
    setNoShowProblem(null);
    setAttendanceUncertain(false);
    setUndoResult(null);
    try {
      const result = await measureAction("attendance.no_show", () =>
        markAppointmentNoShow(ameliaAppointmentId, key),
      );
      if (result?.appointment?.id !== ameliaAppointmentId || result.appointment.status !== "no-show") {
        setAttendanceUncertain(true);
        setNoShowProblem("The booking service did not confirm this lesson’s no-show status. Check the authoritative lesson and credits before retrying.");
        return;
      }
      attendanceAttempt.current = null;
      attendanceNeedsRefresh.current = true;
      setNoShowOverride(true);
      setNoShowResult(result);

      // No-show is not a payment mutation, but re-read the live classification
      // so the drawer never carries a stale payment/credit statement across an
      // attendance write if backend policy ever adds a related effect.
      if (paymentReadable) {
        try {
          const rows = await diaryPaymentClassifications([ameliaAppointmentId]);
          setPayment(rows.find((row) => row.appointment_id === ameliaAppointmentId) ?? null);
          setPaymentProblem(null);
        } catch (cause: unknown) {
          setPayment(null);
          setPaymentProblem(cause instanceof Error ? cause.message : "Payment state is unavailable.");
        }
      }
    } catch (cause: unknown) {
      const uncertain = attendanceFailureIsUncertain(cause);
      setAttendanceUncertain(uncertain);
      if (!uncertain && attendanceAttempt.current?.kind === "no-show") attendanceAttempt.current = null;
      setNoShowProblem(cause instanceof Error ? cause.message : "Could not record the no-show.");
    } finally {
      attendanceInFlight.current = false;
      setBusy(false);
    }
  }

  async function confirmUndoNoShow() {
    if (!isNoShow || !canMutate || busy || attendanceInFlight.current || ameliaAppointmentId === null) return;
    attendanceInFlight.current = true;
    const key = attendanceKey("undo");
    attendancePresented.current = false;
    setBusy(true);
    setNoShowProblem(null);
    setAttendanceUncertain(false);
    setUndoResult(null);
    try {
      const result = await measureAction("attendance.undo_no_show", () =>
        undoAppointmentNoShow(ameliaAppointmentId, key),
      );
      if (result?.appointment?.id !== ameliaAppointmentId || result.appointment.status !== "approved") {
        setAttendanceUncertain(true);
        setNoShowProblem("The booking service did not confirm the attendance reversal. Check the authoritative lesson and credits before retrying.");
        return;
      }
      attendanceAttempt.current = null;
      attendanceNeedsRefresh.current = true;
      // The diary refresh is asynchronous. Update the open drawer's local
      // attendance projection immediately so it stops offering Undo and
      // restores the ordinary actions without requiring close/reopen.
      setNoShowOverride(false);
      setNoShowResult(null);
      setAction(null);
      setUndoResult({ variant: result.credit_review_required ? "attention" : "success", title: "No-show undone", description: `${studentName} · ${formatDate(session.startsAt)} · ${formatTime(session.startsAt)}`,
        details: [{ value: "This lesson shows as booked again." }, { value: result.credits_restored === 0 ? "The booking service restored no lesson credits." : `The booking service reports ${result.credits_restored} lesson credit${result.credits_restored === 1 ? "" : "s"} restored.` }, ...(result.credit_review_required ? [{ value: "The lesson credit effect needs review. Check the authoritative credits before another correction." }] : [])] });
    } catch (cause: unknown) {
      const uncertain = attendanceFailureIsUncertain(cause);
      setAttendanceUncertain(uncertain);
      if (!uncertain && attendanceAttempt.current?.kind === "undo") attendanceAttempt.current = null;
      setNoShowProblem(
        cause instanceof Error ? cause.message : "Could not undo the no-show.",
      );
    } finally {
      attendanceInFlight.current = false;
      setBusy(false);
    }
  }

  async function saveLessonAttendance(status: Exclude<LessonAttendanceStatus, null>) {
    if (!attendanceChipsEligible || first === undefined || lessonAttendanceSaving) return;
    const previous = lessonAttendance;
    const requested = previous === status ? null : status;
    setLessonAttendanceSaving(true);
    setLessonAttendanceProblem(null);
    try {
      const result = await updateLessonAttendance(first.bookingId, requested);
      setLessonAttendance(result.attendanceStatus);
      onRefresh?.();
    } catch (cause: unknown) {
      setLessonAttendance(previous);
      setLessonAttendanceProblem(cause instanceof Error ? cause.message : "Could not update attendance.");
    } finally {
      setLessonAttendanceSaving(false);
    }
  }

  return (
    <Drawer
      eyebrow="Appointment"
      title={mine.map((entry, index) => (
        <span key={entry.bookingId}>
          {index > 0 ? ", " : null}
          {canUseTeachingTools && entry.studentId !== null ? (
            <Link to={`/students/${encodeURIComponent(entry.studentId)}`}>
              {nameOf(entry) ?? "Unknown"}
            </Link>
          ) : (
            nameOf(entry) ?? "Unknown"
          )}
        </span>
      ))}
      description={(
        <>
          {serviceName} · {formatDate(session.startsAt)} · {formatTime(session.startsAt)}–{formatTime(session.endsAt)}
          {first.tutorName ? ` · ${first.tutorName}` : null}
        </>
      )}
      onClose={() => {
        retainAttendanceReceipt();
        const attendanceChanged = attendanceNeedsRefresh.current;
        attendanceNeedsRefresh.current = false;
        closeLessonActionDrawer(action, onClose, onChanged, studentMatched, attendanceChanged);
      }}
      busy={busy}
      closeLabel="Close lesson details"
      className={`cal-lesson-drawer${action === null ? " cal-lesson-drawer--actions" : ""}`}
    >
      <div className="cal-drawer-status cal-status-row" role="group" aria-label="Appointment status">
        {isCancelled && <span className="tag warn">Cancelled</span>}
        {isNoShow && <span className="tag warn">No-show</span>}
        {paymentLoading ? (
          <span className="small muted">Checking payment…</span>
        ) : paymentState !== null ? (
          <span className={`tag${paymentNeedsAttention ? " warn" : ""}`}>{paymentState}</span>
        ) : null}
        {canOverridePayment && (
          <button type="button" className="quiet small cal-status-action" disabled={busy} onClick={() => setAction("payment-override")}>
            {payment?.payment_override ? "Undo payment override" : "Waive balance"}
          </button>
        )}
      </div>
      {payment?.payment_override && (
        <p className="small muted">£{payment.payment_override.amount_waived} waived by {payment.payment_override.set_by_name} on {formatDate(payment.payment_override.created_at)} at {formatTime(payment.payment_override.created_at)}{payment.payment_override.reason.trim() ? ` · ${payment.payment_override.reason}` : ""}</p>
      )}
      {lessonPrice !== null && (
        <p className="small muted">Lesson price · <strong>{lessonPrice}</strong></p>
      )}

      {action === null ? (
        <>
          {primaryActions.length > 0 && (
            <div className="cal-lesson-primary-actions cal-primary-row" role="group" aria-label="Primary appointment actions">
              {primaryActions.map(renderRegisteredAction)}
            </div>
          )}

          <div className="cal-drawer-action-body">
            {(noShowResult !== null || undoResult !== null || (attendanceUncertain && noShowProblem !== null)) && (
              <ActionConfirmation
                {...attendanceReceipt()!}
                onPresented={() => { attendancePresented.current = true; }}
                action={attendanceUncertain ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void (attendanceAttempt.current?.kind === "undo" ? confirmUndoNoShow() : confirmNoShow())}
                  >
                    {busy ? "Checking…" : "Check again"}
                  </button>
                ) : noShowResult !== null ? (
                  <button type="button" disabled={busy} onClick={() => void confirmUndoNoShow()}>
                    {busy ? "Undoing no-show…" : "Undo no-show"}
                  </button>
                ) : undefined}
              />
            )}
            {noShowProblem && !attendanceUncertain && (
              <div className="stack">
                <h3 style={{ marginTop: 0 }}>Attendance update</h3>
                <div className="error" role="alert">{noShowProblem}</div>
              </div>
            )}

            {canTakePayment && ameliaAppointmentId !== null && first.studentId !== null && (
              <div className="cal-payment-row" role="group" aria-label="Payment actions">
                <PaymentPanel
                  layout="inline"
                  appointmentId={ameliaAppointmentId}
                  studentId={first.studentId}
                  payment={payment!}
                  moneyOnly={isCancelled}
                  lessonDescription={`${studentName} · ${formatDate(session.startsAt)} · ${formatTime(session.startsAt)}`}
                  onBusyChange={setBusy}
                  onChanged={changedInPlace}
                />
              </div>
            )}
            {paymentState?.startsWith("Paid") && (
              <div className="cal-paid-payment-row" role="group" aria-label="Paid payment actions">
                <span className="small muted">{paymentState}</span>
                {reversePaymentAction?.enabled && (
                  <button type="button" className="quiet small" disabled={busy} onClick={() => openRegisteredAction(reversePaymentAction)}>
                    Mark as unpaid
                  </button>
                )}
              </div>
            )}
            {!paymentAction?.enabled && paymentAction?.disabledReason && (
              <p className="small muted cal-payment-disabled-reason">{paymentAction.disabledReason}</p>
            )}

            {(attendanceActions.length > 0 || attendanceChipsEligible) && (
              <div className="cal-attendance-row" role="group" aria-label="Attendance actions">
                {attendanceActions.map(renderRegisteredAction)}
                {attendanceChipsEligible && (
                  <>
                    <button
                      type="button"
                      className="quiet small"
                      aria-pressed={lessonAttendance === "present"}
                      disabled={lessonAttendanceSaving}
                      onClick={() => void saveLessonAttendance("present")}
                    >
                      Attended
                    </button>
                    <button
                      type="button"
                      className="quiet small"
                      aria-pressed={lessonAttendance === "late"}
                      disabled={lessonAttendanceSaving}
                      onClick={() => void saveLessonAttendance("late")}
                    >
                      Late
                    </button>
                  </>
                )}
              </div>
            )}
            {lessonAttendanceProblem && <div className="error" role="alert">{lessonAttendanceProblem}</div>}

            {(quickActions.length > 0 || moreActions.length > 0) && (
              <details className="cal-drawer-more">
                <summary>More</summary>
                <div className="cal-drawer-more-actions" role="group" aria-label="Additional appointment actions">
                  {[...quickActions, ...moreActions].map(renderRegisteredAction)}
                </div>
              </details>
            )}

            {!hasAmeliaAppointment && (
              <p className="small muted">This lesson is not an Amelia-backed appointment, so its existing specialist flow remains authoritative.</p>
            )}
            {hasAmeliaAppointment && !canManage && (
              <p className="small muted">This appointment is read-only here. Use the management flow you are authorised for.</p>
            )}
            {paymentReadable && !paymentLoading && payment === null && (
              <p className="small muted">Payment state is unavailable, so no paid/credit claim or payment action is shown.</p>
            )}
            {paymentProblem && <div className="error small">{paymentProblem}</div>}
          </div>
        </>
      ) : (
        <>
          {!studentMatched && (
            <button
              ref={action === "cancel" ? cancelReturnFocusRef : null}
              className="quiet small cal-drawer-back"
              type="button"
              onClick={() => setAction(null)}
              disabled={busy}
            >
              ← Appointment details
            </button>
          )}

          {action === "student-assignment" && matchingBookingId !== null && canMatchStudent && (
            <>
              <MoveLesson
                key={matchingBookingId}
                bookingId={matchingBookingId}
                studentName={null}
                embedded
                onBusyChange={setBusy}
                onMoved={() => setStudentMatched(true)}
              />
              {studentMatched && <button type="button" onClick={onChanged}>Done</button>}
            </>
          )}

          {canMutate && ameliaAppointmentId !== null && action === "move" && (
            <TutorMoveLesson
              key={`${ameliaAppointmentId}:${initialMoveTarget ?? "click"}`}
              bookingId={first.bookingId}
              appointmentId={ameliaAppointmentId}
              studentName={studentName}
              startsAt={session.startsAt}
              serviceName={first.serviceName}
              initialStartsAt={initialMoveTarget}
              recoveryUserId={auth?.me?.user.id ?? "staff"}
              onBusyChange={setBusy}
              onClose={onChanged}
              onMoved={(startsAt) => onScheduleChanged?.({
                kind: "moved",
                appointmentExternalId: ameliaAppointmentId,
                startsAt: movedInstant(startsAt),
              })}
            />
          )}

          {canMutate && ameliaAppointmentId !== null && action === "cancel" && (
            <TutorCancelLesson
              appointmentId={String(ameliaAppointmentId)}
              bookingId={first.bookingId}
              studentName={studentName}
              startsAt={session.startsAt}
              serviceName={first.serviceName}
              presentation="inline"
              onBusyChange={setBusy}
              onClose={({ changed }) => {
                // Dismissing the cancellation section closes only that view. The diary is
                // re-read, closing the drawer, only when the lesson changed.
                if (changed) {
                  onChanged();
                  return;
                }
                restoreCancelOpener.current = true;
                setAction(null);
              }}
              returnFocusRef={cancelReturnFocusRef}
              onCancelled={(appointmentExternalId) => {
                cancelReported.current = true;
                onScheduleChanged?.({ kind: "cancelled", appointmentExternalId });
              }}
              onRestored={() => onScheduleChanged?.()}
            />
          )}

          {canOverridePayment && payment && ameliaAppointmentId !== null && action === "payment-override" && (
            <PaymentOverridePanel appointmentId={ameliaAppointmentId} payment={payment} onBusyChange={setBusy} onChanged={changedInPlace} />
          )}

          {canTakePayment && ameliaAppointmentId !== null && action === "payment" && first.studentId !== null && (
            <PaymentPanel
              appointmentId={ameliaAppointmentId}
              studentId={first.studentId}
              payment={payment}
              moneyOnly={isCancelled}
              lessonDescription={`${studentName} · ${formatDate(session.startsAt)} · ${formatTime(session.startsAt)}`}
              onBusyChange={setBusy}
              onChanged={changedInPlace}
            />
          )}

          {canReversePayment && ameliaAppointmentId !== null && action === "payment-reversal" && first.studentId !== null && (
            <UndoPaymentRecord
              appointmentId={ameliaAppointmentId}
              paymentId={payment?.payment_id ?? null}
              studentId={first.studentId}
              creditFunded={canReverseCredit}
              lessonDescription={`${studentName} · ${formatDate(session.startsAt)} · ${formatTime(session.startsAt)}`}
              idempotencyKey={reversalKey}
              onBusyChange={setBusy}
              onChanged={changedInPlace}
            />
          )}

          {canSetPracticeTodo && action === "practice-todo" && first.studentId !== null && (
            <PracticeTodoPanel
              studentId={first.studentId}
              studentName={nameOf(first) ?? "student"}
              onBusyChange={setBusy}
            />
          )}

          {blockSale !== null && action === "add-credits" && first.studentId !== null && (
            <BlockSaleWorkflow
              canManage={canManage}
              studentId={first.studentId}
              studentName={studentName}
              providerId={first.externalProviderId}
              serviceName={first.serviceName}
              onBusyChange={setBusy}
              onChanged={changedInPlace}
              onCancel={() => setAction(null)}
            />
          )}

          {isNoShow && canMutate && ameliaAppointmentId !== null && action === "undo-no-show" && (
            <div className="stack">
              <h3 style={{ marginTop: 0 }}>Undo no-show</h3>
              <p>This lesson will show as booked again. Any lesson credit it used stays used.</p>
              {attendanceUncertain && noShowProblem ? (
                <ActionConfirmation
                  {...attendanceReceipt()!}
                  onPresented={() => { attendancePresented.current = true; }}
                  action={<button type="button" disabled={busy} onClick={() => void confirmUndoNoShow()}>{busy ? "Checking…" : "Check again"}</button>}
                />
              ) : (
                <>
                  {noShowProblem && <div className="error" role="alert">{noShowProblem}</div>}
                  <button type="button" disabled={busy} onClick={() => void confirmUndoNoShow()}>
                    {busy ? "Undoing no-show…" : "Undo no-show"}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}

function nameOf(entry: DiaryEntry & { anonymous: false }): string | null {
  return entry.studentName ?? entry.customerName;
}
